import React, { useCallback, useState } from 'react';
import { Image, Linking, Pressable, ScrollView, Text, View } from 'react-native';
import { confirmAction, showError, showSuccess } from '../../toast';
import { showPhoto } from '../../photo-viewer';
import { cityLabel, regionOfCity } from '../../geo';
import { useFocusEffect } from '@react-navigation/native';
import { api, API_URL, money, monthLabel, PAYMENT_STATUS_LABELS, UTILITY_LABELS } from '../../api';
import { Badge, Button, Card, COLORS, Label, Muted, Row, Screen, Title } from '../../ui';
import { getLocale, t } from '../../i18n';

// Функция, а не константа — етикетите се преизчисляват при смяна на езика
const METHOD_SHORT = (): Record<string, string> => ({
  SEPA_DEBIT: t('Автоматично теглене'),
  BANK_TRANSFER: t('По банка'),
  CASH: t('В брой'),
});

export default function ApartmentDetailScreen({ route, navigation }: any) {
  const { id } = route.params;
  const [apartment, setApartment] = useState<any>(null);
  // Последната обява за ТОЗИ апартамент — публикуването оттук я подновява,
  // вместо всеки път да се създава нова.
  const [listing, setListing] = useState<any>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  // Раздели вместо дълъг скрол — показва се само една група карти наведнъж.
  const [section, setSection] = useState<'tenant' | 'money' | 'docs' | 'more'>('tenant');

  const load = useCallback(() => {
    api(`/apartments/${id}`).then((r) => setApartment(r.apartment)).catch(() => {});
    // документите на ТОЗИ имот — за раздел „Документи“
    api('/documents')
      .then((r) => {
        const group = (r.groups ?? []).find((g: any) => g.apartmentId === id);
        setDocuments(group ? group.tenants.flatMap((t: any) => t.documents) : []);
      })
      .catch(() => {});
    api('/listings/mine')
      .then((r) => {
        const mine = r.listings.filter((l: any) => l.apartmentId === id);
        setListing(mine[0] ?? null); // /listings/mine е сортиран по createdAt desc
      })
      .catch(() => {});
  }, [id]);

  useFocusEffect(load);

  if (!apartment) return <Screen><Muted>{t('Зареждане…')}</Muted></Screen>;

  const tenancy = apartment.tenancies.find((t: any) => t.status === 'ACTIVE' || t.status === 'INVITED');
  // Пропърти мениджърът управлява ЧУЖД имот — може да прекрати договора с наемодателя.
  // Прекратяването замразява имота, докато собственикът реши как да продължи.
  const contract = apartment.managementContracts?.[0];

  const act = async (path: string) => {
    try {
      await api(path, { body: {} });
      load();
    } catch (e: any) {
      showError(e.message);
    }
  };

  // Напомняне към наемателя за неплатено задължение
  const remind = async (path: string) => {
    try {
      await api(path, { body: {} });
      showSuccess(t('Напомнихме на наемателя.'));
    } catch (e: any) {
      showError(e.message);
    }
  };

  const generate = async (path: string, body: object) => {
    try {
      const res = await api(path, { body });
      showSuccess(t('Готово: {title}', { title: res.document.title }));
    } catch (e: any) {
      showError(e.message);
    }
  };

  // Освободен имот → освежаване с два клика: нередност „Почистване след
  // изнасяне" и право към майсторите с предизбрана категория.
  const offerCleaning = () => {
    confirmAction(
      t('Имотът се освободи'),
      t('Трябва ли ви почистване или ремонт преди следващия наемател? Ще поискаме оферти от майсторите в областта.'),
      t('Поискай оферти'),
      async () => {
        try {
          const r = await api('/issues', {
            body: {
              apartmentId: id,
              title: t('Освежаване след изнасяне'),
              description: t('Наемателят се изнесе — търсим оферти за почистване/освежаване преди следващото настаняване.'),
            },
          });
          navigation.navigate('HandymenPick', {
            issueId: r.issue.id,
            presetSpecialty: 'Почистване',
            region: apartment.region ?? regionOfCity(apartment.city),
          });
        } catch (e: any) {
          showError(e.message);
        }
      }
    );
  };

  const endTenancy = () => {
    const endedTenant = tenancy?.tenant ? `${tenancy.tenant.firstName} ${tenancy.tenant.lastName}` : null;
    const endedTenancyId = tenancy?.id;
    confirmAction(t('Приключване'), t('Наистина ли приключвате наемното отношение?'), t('Приключи'), async () => {
      try {
        await api(`/tenancies/${endedTenancyId}/end`, { body: {} });
        load();
        // ПЪРВО оценката на бившия наемател (отзивите са гръбнакът на
        // проверката по телефон), ПОСЛЕ предложението за почистване.
        // Пропусне ли я — тя остава в „Чака действие“ на дашборда.
        if (endedTenant) {
          confirmAction(
            t('Оценете бившия наемател'),
            t('{name} вече не е ваш наемател. Оставете отзив — той помага на следващия наемодател при проверката по телефон. Ако пропуснете сега, напомняне ще чака на началния екран.', { name: endedTenant }),
            t('Оцени наемателя ★'),
            () => navigation.navigate('Review', { tenancyId: endedTenancyId, targetName: endedTenant }),
            { onCancel: offerCleaning, cancelLabel: t('По-късно') }
          );
        } else {
          offerCleaning();
        }
      } catch (e: any) {
        showError(e.message);
      }
    });
  };

  return (
    <Screen>
      <Title>{apartment.address}</Title>
      <Muted>{cityLabel(apartment.city, apartment.region)}{apartment.area ? ` · ${t('{n} кв.м', { n: apartment.area })}` : ''}{apartment.rooms ? ` · ${t('{n} стаи', { n: apartment.rooms })}` : ''}</Muted>

      <Card>
        <Button
          title={t('📊 Статистика на имота')}
          variant="secondary"
          onPress={() => navigation.navigate('ApartmentStats', { id, address: apartment.address })}
        />
      </Card>

      {apartment.photos.length > 0 && (
        <ScrollView horizontal style={{ marginVertical: 12 }}>
          {apartment.photos.map((p: string) => (
            <Pressable key={p} onPress={() => showPhoto(`${API_URL}${p}`)}>
              <Image source={{ uri: `${API_URL}${p}` }} style={{ width: 140, height: 100, borderRadius: 10, marginRight: 8 }} />
            </Pressable>
          ))}
        </ScrollView>
      )}

      {/* Раздели — една група карти наведнъж вместо дълъг скрол */}
      <View style={{ flexDirection: 'row', backgroundColor: COLORS.bg, borderRadius: 10, padding: 4, marginBottom: 12 }}>
        {(
          [
            ['tenant', t('Наемател')],
            ['money', t('Пари')],
            ['docs', t('Документи')],
            ['more', t('Имот')],
          ] as const
        ).map(([key, lbl]) => (
          <Pressable
            key={key}
            onPress={() => setSection(key)}
            style={{ flex: 1, paddingVertical: 9, borderRadius: 8, alignItems: 'center', backgroundColor: section === key ? COLORS.card : 'transparent' }}
          >
            <Text style={{ fontSize: 13, fontWeight: section === key ? '700' : '500', color: section === key ? COLORS.text : COLORS.muted }}>
              {lbl}
            </Text>
          </Pressable>
        ))}
      </View>

      {section === 'more' && !contract && apartment.invitations?.[0] && (
        // Собственикът е поканен, но още не е влязъл — да се вижда кой е
        <Card>
          <Label>{t('Собственик (поканен)')}</Label>
          <Row
            left={t('Име')}
            right={
              [apartment.invitations[0].firstName, apartment.invitations[0].lastName].filter(Boolean).join(' ') || '—'
            }
          />
          <Row left={t('Телефон')} right={apartment.invitations[0].phone} />
          <Muted>{t('Изпратен е SMS с покана. Договорът за управление се създава при първия му вход.')}</Muted>
        </Card>
      )}

      {section === 'more' && contract && (
        <Card>
          <Label>{t('Договор за управление')}</Label>
          <Row
            left={t('Собственик')}
            right={`${contract.owner.firstName} ${contract.owner.lastName}`}
          />
          <Row left={t('Телефон')} right={contract.owner.phone} />
          <Row left={t('Процент за управление')} right={`${Number(contract.managementPercent)}%`} />
          <Muted>
            {t('При прекратяване имотът се замразява, докато собственикът не реши да го поеме сам или да го даде на друг пропърти мениджър.')}
          </Muted>
          <Button
            title={t('Прекрати управлението')}
            variant="danger"
            onPress={() =>
              confirmAction(
                t('Прекратяване на управлението'),
                t('Наистина ли прекратявате управлението на {address}? Имотът ще се замрази.', { address: apartment.address }),
                t('Прекрати'),
                async () => {
                  try {
                    await api(`/management-contracts/${contract.id}/terminate`, { body: {} });
                    showSuccess(t('Управлението е прекратено. Имотът е замразен.'));
                    navigation.goBack();
                  } catch (e: any) {
                    showError(e.message);
                  }
                }
              )
            }
          />
        </Card>
      )}

      {section === 'tenant' && (
      <Card>
        <Label>{t('Получаване на наема')}</Label>
        <Row left={t('Начин')} right={METHOD_SHORT()[apartment.rentCollectionMethod] ?? apartment.rentCollectionMethod} />
        <Button
          title={t('Промени начина')}
          variant="secondary"
          onPress={() =>
            navigation.navigate('PaymentMethod', {
              apartmentId: id,
              currentMethod: apartment.rentCollectionMethod,
              monthlyRentCents: tenancy?.monthlyRentCents ?? null,
            })
          }
        />
      </Card>
      )}

      {section === 'tenant' && (
      <Card>
        <Label>{t('Наемател')}</Label>
        {tenancy ? (
          <>
            <Row
              left={t('Наемател')}
              right={tenancy.tenant ? `${tenancy.tenant.firstName} ${tenancy.tenant.lastName}` : t('{phone} (чака регистрация)', { phone: tenancy.invitedPhone })}
            />
            <Row left={t('Телефон')} right={tenancy.tenant?.phone ?? tenancy.invitedPhone} />
            <Button
              title={t('📞 Позвъни на наемателя')}
              variant="secondary"
              onPress={() => Linking.openURL(`tel:${(tenancy.tenant?.phone ?? tenancy.invitedPhone).replace(/\s/g, '')}`)}
            />
            <Row left={t('Наем')} right={t('{amount}/мес.', { amount: money(tenancy.monthlyRentCents) })} />
            <Row left={t('Падеж')} right={t('{day}-о число', { day: tenancy.dueDay })} />
            {tenancy.bankReferenceBase && <Row left={t('Код за превод')} right={tenancy.bankReferenceBase} />}
            {tenancy.depositCents ? (
              <Row
                left={t('Депозит')}
                right={`${money(tenancy.depositCents)} · ${
                  tenancy.depositPaidAt
                    ? t('получен {date}', { date: new Date(tenancy.depositPaidAt).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') })
                    : tenancy.depositMethod === 'BANK'
                      ? t('по банка — чака плащане')
                      : t('в брой — чака')
                }`}
              />
            ) : null}
            {tenancy.startDate && (
              <Row
                left={t('Договор')}
                right={`${t('от {date}', { date: new Date(tenancy.startDate).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') })}${
                  tenancy.termMonths ? ` · ${t('{n} мес.', { n: tenancy.termMonths })}` : ` · ${t('безсрочен')}`
                }`}
              />
            )}
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 4 }}>
              <View style={{ flex: 1 }}>
                <Button
                  title={t('Протокол')}
                  variant="secondary"
                  onPress={() => navigation.navigate('CreateHandover', { tenancyId: tenancy.id })}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Button title={t('Приключи')} variant="danger" onPress={endTenancy} />
              </View>
            </View>
          </>
        ) : (
          <>
            <Muted>{t('Няма наемател.')}</Muted>
            <Button title={t('+ Добави наемател по телефон')} onPress={() => navigation.navigate('AddTenant', { apartmentId: id })} />
            {/* Има ли вече обява за имота — подновяваме СЪЩАТА (пази история
                и чатове); нова се създава само първия път. */}
            {!listing && (
              <Button
                title={t('Публикувай обява 🔎')}
                variant="secondary"
                onPress={() => navigation.navigate('CreateListing', { apartmentId: id })}
              />
            )}
            {listing && (listing.status === 'APPROVED' || listing.status === 'PENDING_REVIEW') && (
              <Muted>
                {listing.status === 'APPROVED'
                  ? t('✓ Обявата е публикувана — вижте я в „Свободни апартаменти" → Моите обяви.')
                  : t('Обявата чака одобрение от модератор.')}
              </Muted>
            )}
            {listing && (listing.status === 'ARCHIVED' || listing.status === 'REJECTED' || listing.status === 'DRAFT') && (
              <>
                {listing.status === 'REJECTED' && listing.rejectionReason ? (
                  <Muted>{t('Обявата е отхвърлена: {reason}', { reason: listing.rejectionReason })}</Muted>
                ) : null}
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <View style={{ flex: 1 }}>
                    <Button
                      title={t('Публикувай пак обява')}
                      onPress={async () => {
                        try {
                          await api(`/listings/${listing.id}/submit`, { body: {} });
                          showSuccess(t('Обявата е изпратена за одобрение.'));
                          load();
                        } catch (e: any) {
                          showError(e.message);
                        }
                      }}
                    />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Button
                      title={t('Редактирай обява')}
                      variant="secondary"
                      onPress={() => navigation.navigate('CreateListing', { apartmentId: id, listingId: listing.id })}
                    />
                  </View>
                </View>
              </>
            )}
          </>
        )}
      </Card>

      )}

      {section === 'tenant' && (
      <Card>
        <Label>{t('Ремонт / поддръжка')}</Label>
        <Muted>
          {tenancy?.tenant
            ? t('Заявете ремонт за имота. Освобождаването на плащането се потвърждава от вас и наемателя.')
            : t('Няма наемател — ремонтът се потвърждава само от вас.')}
        </Muted>
        <Button
          title={t('🛠️ Заяви ремонт')}
          variant="secondary"
          onPress={() => navigation.navigate('NewIssue', { apartmentId: id })}
        />
      </Card>
      )}

      {section === 'tenant' && apartment.tenancies.filter((t: any) => t.status === 'ENDED').length > 0 && (
        <Card>
          <Label>{t('Минали наематели')}</Label>
          {apartment.tenancies
            .filter((t: any) => t.status === 'ENDED')
            .map((tn: any) => (
              <View key={tn.id} style={{ paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: COLORS.border }}>
                <Row
                  left={tn.tenant ? `${tn.tenant.firstName} ${tn.tenant.lastName}` : tn.invitedPhone}
                  right={t('до {date}', { date: tn.endDate ? new Date(tn.endDate).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') : '—' })}
                />
                {tn.tenant && (
                  <Button
                    title={t('Оцени наемателя ★')}
                    variant="secondary"
                    onPress={() =>
                      navigation.navigate('Review', {
                        tenancyId: tn.id,
                        targetName: `${tn.tenant.firstName} ${tn.tenant.lastName}`,
                      })
                    }
                  />
                )}
              </View>
            ))}
        </Card>
      )}

      {section === 'money' && tenancy?.rentPayments?.length > 0 && (
        <Card>
          <Label>{t('Наеми')}</Label>
          {tenancy.rentPayments.map((p: any) => (
            <View key={p.id} style={{ paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: COLORS.border }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text style={{ color: COLORS.text }}>
                  {p.kind === 'DEPOSIT'
                    ? t('Депозит')
                    : p.kind === 'PRORATA'
                      ? t('Частичен наем · {month}', { month: monthLabel(p.periodMonth, p.periodYear) })
                      : monthLabel(p.periodMonth, p.periodYear)}
                </Text>
                <Text style={{ color: COLORS.text, fontWeight: '500' }}>{money(p.amountCents)}</Text>
                <Badge
                  text={t(PAYMENT_STATUS_LABELS[p.status] ?? p.status)}
                  color={p.status === 'PAID' ? COLORS.success : p.status === 'PENDING' ? '#D97706' : COLORS.primary}
                />
              </View>
              {p.status === 'PAID' && p.providerFeeCents ? (
                <Muted>
                  {t('Такса за превода −{fee}', { fee: money(p.providerFeeCents) })}
                  {p.agencyShareCents ? ` · ${t('пропърти мениджър {agency} · собственик {owner}', { agency: money(p.agencyShareCents), owner: money(p.ownerShareCents) })}` : ''}
                </Muted>
              ) : null}
              {/* Наемателят е отбелязал банков превод — показваме платежното
                  и разчетеното. Парите се отчитат САМО с „Получено“. */}
              {p.bankDeclaredAt && p.status !== 'PAID' && (
                <View style={{ marginTop: 4, padding: 8, backgroundColor: COLORS.bg, borderRadius: 8 }}>
                  {p.slipCodeFound === true && <Badge text={t('Кодът {code} е вписан ✓', { code: p.referenceCode ?? '' })} color={COLORS.success} />}
                  {p.slipCodeFound === false && (
                    <Badge text={t('Кодът НЕ се открива в платежното — проверете внимателно')} color={COLORS.danger} />
                  )}
                  {p.slipCodeFound == null && (
                    <Muted>
                      {p.slipPhotoUrl
                        ? t('Платежното не беше разчетено — проверете сметката си.')
                        : t('Без приложено платежно — проверете сметката си за кода.')}
                    </Muted>
                  )}
                  {p.slipAmountCents != null && p.slipAmountCents !== p.amountCents && (
                    <Muted>
                      {t('⚠️ В платежното: {slip} · дължимо: {due} (разлика {diff})', {
                        slip: money(p.slipAmountCents),
                        due: money(p.amountCents),
                        diff: money(Math.abs(p.slipAmountCents - p.amountCents)),
                      })}
                    </Muted>
                  )}
                  {p.slipPhotoUrl && (
                    <Pressable onPress={() => showPhoto(`${API_URL}${p.slipPhotoUrl}`)}>
                      <Image
                        source={{ uri: `${API_URL}${p.slipPhotoUrl}` }}
                        style={{ width: 100, height: 100, borderRadius: 8, marginTop: 6 }}
                      />
                    </Pressable>
                  )}
                  <Muted>{t('Снимката е нареждане, не гаранция — потвърдете чак след като видите парите.')}</Muted>
                  <Button title={t('Получено ✓')} onPress={() => act(`/payments/${p.id}/mark-received`)} />
                </View>
              )}
              {p.status === 'PENDING' && p.method === 'BANK_TRANSFER' && !p.bankDeclaredAt && (
                <Button title={t('Получено по банка ✓')} variant="secondary" onPress={() => act(`/payments/${p.id}/mark-received`)} />
              )}
              {p.status === 'PENDING' && p.method === 'CASH' && (
                <Button title={t('Получих в брой')} variant="secondary" onPress={() => act(`/payments/${p.id}/cash-initiate`)} />
              )}
              {/* Неплатено задължение → напомняне към наемателя */}
              {p.status === 'PENDING' && tenancy?.tenant && (
                <Button title={t('🔔 Напомни на наемателя')} variant="secondary" onPress={() => remind(`/payments/${p.id}/remind`)} />
              )}
              {p.status === 'AWAITING_CONFIRMATION' && p.cashInitiatedBy === 'TENANT' && (
                <Button title={t('Потвърди: получих в брой ✓')} onPress={() => act(`/payments/${p.id}/cash-confirm`)} />
              )}
              {p.status === 'AWAITING_CONFIRMATION' && p.cashInitiatedBy === 'MANAGER' && (
                <Muted>{t('Чака потвърждение от наемателя.')}</Muted>
              )}
            </View>
          ))}
        </Card>
      )}

      {section === 'docs' && (
        <Card>
          <Label>{t('Документи (PDF)')}</Label>
          {tenancy ? (
            <>
              <Muted>{t('Генерират се с данните на двете страни и влизат в архива.')}</Muted>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <View style={{ flex: 1 }}>
                  <Button title={t('Договор')} variant="secondary" onPress={() => navigation.navigate('Contract', { tenancyId: tenancy.id })} />
                </View>
                <View style={{ flex: 1 }}>
                  <Button title={t('Фактура')} variant="secondary" onPress={() => navigation.navigate('IssueInvoice', { tenancyId: tenancy.id })} />
                </View>
              </View>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <View style={{ flex: 1 }}>
                  <Button title={t('Разписка депозит')} variant="secondary" onPress={() => generate('/documents/deposit-receipt', { tenancyId: tenancy.id })} />
                </View>
                <View style={{ flex: 1 }}>
                  <Button title={t('Предизвестие')} variant="secondary" onPress={() => generate('/documents/notice', { tenancyId: tenancy.id })} />
                </View>
              </View>
            </>
          ) : (
            <Muted>{t('Добавете наемател, за да генерирате договор и документи. Старите документи на имота остават тук.')}</Muted>
          )}
        </Card>
      )}

      {section === 'docs' && (
        <Card>
          <Label>{t('Документи на имота ({n})', { n: documents.length })}</Label>
          {documents.length === 0 && <Muted>{t('Още няма генерирани документи за този имот.')}</Muted>}
          {documents.map((d: any) => (
            <View key={d.id} style={{ paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: COLORS.hair }}>
              <Text style={{ fontSize: 15, fontWeight: '600', color: COLORS.text }}>{d.title}</Text>
              <Muted>{new Date(d.createdAt).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG')}</Muted>
              <Button title={t('Отвори (PDF)')} variant="secondary" onPress={() => Linking.openURL(`${API_URL}${d.fileUrl}`)} />
            </View>
          ))}
          <Button title={t('Всички документи →')} onPress={() => navigation.navigate('Documents')} />
        </Card>
      )}

      {section === 'docs' && apartment.handoverProtocols?.length > 0 && (
        <Card>
          <Label>{t('Приемо-предавателни протоколи')}</Label>
          {apartment.handoverProtocols.map((p: any) => (
            <View key={p.id} style={{ paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: COLORS.border }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <View style={{ flex: 1 }}>
                  <Text style={{ color: COLORS.text }}>
                    {p.type === 'MOVE_IN' ? t('При настаняване') : t('При напускане')} · {t('{n} снимки', { n: p.photos.length })}
                  </Text>
                  <Muted>{new Date(p.createdAt).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG')}</Muted>
                </View>
                <Badge
                  text={p.tenantConfirmedAt ? t('Потвърден от наемателя ✓') : t('Чака потвърждение')}
                  color={p.tenantConfirmedAt ? COLORS.success : '#D97706'}
                />
              </View>
              {p.tenantRemark ? (
                <View style={{ marginTop: 6, padding: 10, backgroundColor: COLORS.bg, borderRadius: 8 }}>
                  <Text style={{ color: COLORS.text }}>{t('Забележка от наемателя: „{remark}“', { remark: p.tenantRemark })}</Text>
                  {p.remarkAcceptedAt ? (
                    <Badge text={t('Приета — част от протокола ✓')} color={COLORS.success} />
                  ) : (
                    <Button
                      title={t('Приемам забележката')}
                      variant="secondary"
                      onPress={() => act(`/tenancies/handover/${p.id}/accept-remark`)}
                    />
                  )}
                </View>
              ) : null}
            </View>
          ))}
        </Card>
      )}

      {section === 'money' && (
      <Card>
        <Label>{t('Сметки')}</Label>
        {apartment.utilityBills.length === 0 && <Muted>{t('Няма добавени сметки.')}</Muted>}
        {apartment.utilityBills.map((b: any) => (
          <View key={b.id} style={{ paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: COLORS.border }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text style={{ color: COLORS.text }}>
                {t(UTILITY_LABELS[b.type])} · {monthLabel(b.periodMonth, b.periodYear)}
              </Text>
              <Text style={{ fontWeight: '500', color: COLORS.text }}>{money(b.amountCents)}</Text>
              <Badge text={b.status === 'PAID' ? t('Платена') : t('Неплатена')} color={b.status === 'PAID' ? COLORS.success : '#D97706'} />
            </View>
            {b.status !== 'PAID' && tenancy?.tenant && (
              <Button
                title={t('🔔 Напомни на наемателя')}
                variant="secondary"
                onPress={() => remind(`/tenancies/bills/${b.id}/remind`)}
              />
            )}
          </View>
        ))}
        <Button title={t('+ Нова сметка')} variant="secondary" onPress={() => navigation.navigate('AddBill', { apartmentId: id })} />
      </Card>
      )}

      {section === 'more' && (
      <Card>
        <Label>{t('ИТН / клиентски номера')}</Label>
        {apartment.utilityAccounts.length === 0 && <Muted>{t('Няма въведени номера.')}</Muted>}
        {apartment.utilityAccounts.map((u: any) => (
          <Row key={u.id} left={`${t(UTILITY_LABELS[u.type])}${u.providerName ? ` (${u.providerName})` : ''}`} right={u.clientNumber} />
        ))}
        <Button
          title={t('+ Добави номер')}
          variant="secondary"
          onPress={() => navigation.navigate('AddUtilityAccount', { apartmentId: id })}
        />
      </Card>
      )}

      {section === 'more' && (
      <Card>
        <Label>{t('Архив')}</Label>
        <Muted>
          {t('За продаден имот или прекратена работа с него. Историята (плащания, документи, протоколи) се запазва — имотът отива в Още → „Архивирани имоти“ и може да се върне оттам.')}
          {tenancy ? ` ${t('Първо приключете наемното отношение.')}` : ''}
        </Muted>
        <Button
          title={t('Архивирай имота')}
          variant="danger"
          disabled={!!tenancy}
          onPress={() =>
            confirmAction(
              t('Архивиране'),
              t('Наистина ли архивирате {address}? Обявата му се сваля, а при пропърти мениджър договорът за управление се прекратява.', { address: apartment.address }),
              t('Архивирай'),
              async () => {
                try {
                  await api(`/apartments/${id}`, { method: 'DELETE' });
                  showSuccess(t('Имотът е в архива.'));
                  navigation.goBack();
                } catch (e: any) {
                  showError(e.message);
                }
              }
            )
          }
        />
      </Card>
      )}
    </Screen>
  );
}
